import React, { useState } from 'react';
import { Building2, CheckCircle2, MapPin, User, Mail, Phone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { hotelDestinations } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = {
  single: { label: 'Single City', price: '499' },
  multiple: { label: 'Multiple Cities', price: '799' },
  complete: { label: 'Complete Package', price: '1,399' },
};

export const HotelBooking = () => {
  const [open, setOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('single');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [destination, setDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const openModal = (pkg) => {
    setSelectedPackage(pkg);
    setError('');
    setSuccess(false);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      setError('Please enter your name and phone number');
      return;
    }
    setError('');
    setSubmitting(true);

    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      package: PACKAGES[selectedPackage].label,
      destination: destination.trim(),
      notes: `Inquiry for ${PACKAGES[selectedPackage].label} (₹${PACKAGES[selectedPackage].price})`,
    };

    try {
      const res = await fetch(`${API}/submissions/hotel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Submission failed');
      await res.json();
      setSuccess(true);

      // GA4 conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'hotel_form_submit', {
          package: PACKAGES[selectedPackage].label,
          price: PACKAGES[selectedPackage].price,
          destination: destination || 'not_specified',
        });
      }

      setTimeout(() => {
        const msg =
          `Hello, I'm interested in the ${PACKAGES[selectedPackage].label} (₹${PACKAGES[selectedPackage].price}) hotel voucher.\n\n` +
          `Name: ${name}\nPhone: ${phone}` +
          (destination ? `\nDestination: ${destination}` : '');
        window.open(`https://wa.me/919353536633?text=${encodeURIComponent(msg)}`, '_blank');
      }, 800);
    } catch (e) {
      setError('Could not submit. Please contact us on WhatsApp directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    setName('');
    setEmail('');
    setPhone('');
    setDestination('');
    setSuccess(false);
    setError('');
  };

  return (
    <section
      id="hotel"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-white to-blue-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 rounded-full text-sm font-medium mb-4">
            <Building2 size={16} className="mr-2" />
            Complete Your Application
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Hotel{' '}
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              Reservation Vouchers
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Strengthen your visa application with verified hotel reservation confirmations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Benefits Card */}
          <Card className="shadow-xl border-0 bg-white">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Why You Need Hotel Vouchers</h3>
              <div className="space-y-3">
                {[
                  'Required by most embassies as proof of accommodation',
                  'Shows your travel itinerary is well-planned',
                  'Verifiable reservation confirmations from real hotels',
                  'No upfront hotel payment required',
                  'Available for destinations worldwide',
                ].map((text, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle2
                      className="text-green-500 mr-3 mt-1 flex-shrink-0"
                      size={20}
                    />
                    <p className="text-gray-700">{text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pricing Card */}
          <Card className="shadow-xl border-0 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Hotel Voucher Packages</h3>
              <div className="space-y-4">
                <div
                  className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-blue-300"
                  onClick={() => openModal('single')}
                  data-testid="package-single"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Single City</h4>
                    <span className="text-2xl font-extrabold text-blue-600">₹499</span>
                  </div>
                  <p className="text-sm text-gray-600">Hotel voucher for one city/destination</p>
                </div>

                <div
                  className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-orange-300"
                  onClick={() => openModal('multiple')}
                  data-testid="package-multiple"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Multiple Cities</h4>
                    <span className="text-2xl font-extrabold text-orange-600">₹799</span>
                  </div>
                  <p className="text-sm text-gray-600">Hotel vouchers for up to 3 cities</p>
                </div>

                <div
                  className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-4 shadow-lg cursor-pointer text-white relative"
                  onClick={() => openModal('complete')}
                  data-testid="package-complete"
                >
                  <div className="absolute -top-2 right-3 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold">Complete Package</h4>
                    <span className="text-2xl font-extrabold">₹1,399</span>
                  </div>
                  <p className="text-sm text-blue-100">Flight + Hotel vouchers bundle</p>
                  <div className="mt-2 text-xs text-yellow-300 font-semibold">Save ₹99!</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Popular Destinations */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Popular Destinations
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {hotelDestinations
              .filter((d) => d.popular)
              .map((dest) => (
                <div
                  key={dest.id}
                  className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 text-center"
                >
                  <MapPin className="mx-auto mb-2 text-orange-500" size={28} />
                  <p className="font-bold text-gray-900">{dest.city}</p>
                  <p className="text-sm text-gray-600">{dest.country}</p>
                </div>
              ))}
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={() => openModal('single')}
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-10 py-6 text-lg font-bold rounded-xl shadow-2xl"
            data-testid="get-hotel-voucher-btn"
          >
            Get Hotel Voucher
          </Button>
        </div>
      </div>

      {/* Hotel Inquiry Dialog */}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(o) : resetAndClose())}>
        <DialogContent className="sm:max-w-md" data-testid="hotel-dialog">
          {success ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-2">Inquiry Received!</h3>
              <p className="text-gray-600 mb-4">Opening WhatsApp to continue...</p>
              <Button onClick={resetAndClose} variant="outline" data-testid="hotel-close-btn">
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  Get {PACKAGES[selectedPackage].label} - ₹{PACKAGES[selectedPackage].price}
                </DialogTitle>
                <DialogDescription>
                  Enter your details and we'll contact you on WhatsApp
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="hotel-name" className="flex items-center">
                    <User size={14} className="mr-1 text-blue-600" />
                    Name <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="hotel-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    data-testid="hotel-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hotel-phone" className="flex items-center">
                    <Phone size={14} className="mr-1 text-blue-600" />
                    Phone (WhatsApp) <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="hotel-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 9876543210"
                    data-testid="hotel-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hotel-email" className="flex items-center">
                    <Mail size={14} className="mr-1 text-blue-600" />
                    Email (optional)
                  </Label>
                  <Input
                    id="hotel-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="hotel-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hotel-destination" className="flex items-center">
                    <MapPin size={14} className="mr-1 text-orange-600" />
                    Destination (optional)
                  </Label>
                  <Input
                    id="hotel-destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="e.g., London, Paris, Dubai"
                    data-testid="hotel-destination-input"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#20BA5A] hover:to-[#0e6e63] text-white"
                  data-testid="hotel-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Submitting...
                    </>
                  ) : (
                    'Submit & Continue on WhatsApp'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
